import { useState } from 'react';
import { ExternalLink, Plus, X } from 'lucide-react';
import { Button, Input, Select } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  LinkFavicon,
  parseUrl,
  PlatformIcon,
  PLATFORM_BRAND,
  PLATFORM_LABEL,
  PLATFORM_URL,
  PLATFORMS,
} from '../socialPlatforms';

/** Valid username chars — letters, digits, dot, underscore, hyphen (no spaces). */
const USERNAME_RE = /^[a-zA-Z0-9._-]+$/;

interface SocialLinksProps {
  links: Record<string, string>;
  /** Self profile → render the add-link row and per-link remove buttons. */
  editable?: boolean;
  onAdd?: (platform: string, url: string) => void;
  onRemove?: (platform: string) => void;
}

/**
 * Social links as larger, full-width boxes — one per platform. On your own
 * profile an add-link row (platform picker + url) sits above the boxes; once
 * every platform is linked the row disappears (a silent cap — no counter shown —
 * so the column can't overflow). Read-only elsewhere: each box is a link out.
 */
export function SocialLinks({ links, editable, onAdd, onRemove }: SocialLinksProps) {
  const entries = Object.entries(links);
  const used = new Set(entries.map(([p]) => p));
  const available = PLATFORMS.filter((p) => !used.has(p.id));

  const [platform, setPlatform] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);

  if (entries.length === 0 && !editable) return null;

  // Silent cap: nothing left to link → no add row.
  const canAdd = Boolean(editable && available.length > 0);
  const selectedPlatform = platform || available[0]?.id || '';
  // Brand the picker badge the same way a saved link box is branded.
  const selectedBrand = PLATFORM_BRAND[selectedPlatform];
  // Social platforms take a username (we build the URL); `web` takes a raw URL.
  const buildUrl = PLATFORM_URL[selectedPlatform];

  const submit = () => {
    if (!selectedPlatform) return;
    if (buildUrl) {
      // Username flow — accept a bare handle (strip a leading @), build the URL.
      const username = url.trim().replace(/^@+/, '');
      if (!USERNAME_RE.test(username)) {
        setError(true);
        return;
      }
      onAdd?.(selectedPlatform, buildUrl(username));
    } else {
      // Website flow — reject anything that isn't a proper link.
      const parsed = parseUrl(url);
      if (!parsed) {
        setError(true);
        return;
      }
      onAdd?.(selectedPlatform, parsed.toString());
    }
    setPlatform('');
    setUrl('');
    setError(false);
  };

  return (
    <div className="mb-6 flex flex-col gap-2.5">
      {canAdd && (
        <div className="rounded-lg border border-card-line bg-white/[0.03] p-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            {/* Platform picker — a brand-glyph badge fronts the dropdown so the
                selected network reads at a glance, not just as text. */}
            <div className="flex flex-none items-center gap-2 sm:w-48">
              <span
                className={cn(
                  'grid h-11 w-11 flex-none place-items-center rounded-md',
                  selectedBrand ? 'text-white' : 'border border-card-line bg-input text-hi',
                )}
                style={selectedBrand ? { background: selectedBrand.badge } : undefined}
              >
                <PlatformIcon id={selectedPlatform} className="h-5 w-5" />
              </span>
              <Select
                value={selectedPlatform}
                onChange={(e) => setPlatform(e.target.value)}
                className="flex-1"
                aria-label="Link platform"
              >
                {available.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </div>
            <Input
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder={buildUrl ? 'username' : 'Paste a link…'}
              aria-label={buildUrl ? 'Username' : 'Link URL'}
              aria-invalid={error}
              className={cn(
                'flex-1',
                error && 'focus:ring-danger/30 border-danger focus:border-danger',
              )}
            />
            <Button type="button" size="sm" onClick={submit} disabled={!url.trim()}>
              <Plus size={15} strokeWidth={2.4} />
              Add
            </Button>
          </div>
          {error && (
            <p className="mt-2 text-[11px] font-semibold text-danger">
              {buildUrl
                ? 'Enter just your username — letters, numbers, dots, dashes.'
                : 'That doesn’t look like a valid link — try something like winr.com.'}
            </p>
          )}
        </div>
      )}

      {entries.map(([p, href]) => {
        const label = PLATFORM_LABEL[p] ?? p;
        // Brand the populated box in the platform's colors (web stays neutral).
        const brand = PLATFORM_BRAND[p];
        const boxStyle = brand
          ? {
              borderColor: `color-mix(in srgb, ${brand.accent} 45%, transparent)`,
              background: `color-mix(in srgb, ${brand.accent} 9%, transparent)`,
            }
          : undefined;
        const inner = (
          <>
            <span
              className={cn(
                'relative grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-md',
                brand ? 'text-white' : 'bg-white/[0.05] text-hi',
              )}
              style={brand ? { background: brand.badge } : undefined}
            >
              {p === 'web' ? <LinkFavicon href={href} /> : <PlatformIcon id={p} className="h-5 w-5" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-bold capitalize text-hi">{label}</span>
              <span className="block truncate text-[11px] text-lo">{href}</span>
            </span>
          </>
        );
        const boxClass =
          'group flex items-center gap-3 rounded-md border border-edge bg-white/[0.03] px-3.5 py-3 transition-colors hover:border-white/15';

        return editable ? (
          <div key={p} className={boxClass} style={boxStyle}>
            {inner}
            <button
              type="button"
              onClick={() => onRemove?.(p)}
              aria-label={`Remove ${label} link`}
              className="hover:bg-danger/[0.12] grid h-7 w-7 flex-none place-items-center rounded-full text-lo transition-colors hover:text-danger"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <a
            key={p}
            href={href}
            target="_blank"
            rel="noreferrer"
            className={cn(boxClass, 'hover:bg-white/[0.03]')}
            style={boxStyle}
          >
            {inner}
            <ExternalLink
              size={15}
              className="flex-none text-lo transition-colors group-hover:text-mid"
            />
          </a>
        );
      })}
    </div>
  );
}
